import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import StatCard from '../../components/cards/StatCard';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/ui/Alert';

const API_URL = 'https://medigo-api.onrender.com/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('medigo_token');
      
      if (!token) {
        setError('Not authenticated. Please login again.');
        setLoading(false);
        return;
      }

      
      const statsRes = await axios.get(`${API_URL}/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }

     
      try {
        const activityRes = await axios.get(`${API_URL}/admin/dashboard/recent-activity`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (activityRes.data.success) {
          setRecentActivity(activityRes.data.data);
        }
      } catch (activityErr) {
        console.error('Recent activity error:', activityErr);
        setRecentActivity([]);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        localStorage.removeItem('medigo_token');
      } else {
        setError(err.response?.data?.message || 'Error loading dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6">
        <Alert type="error" message="Failed to load dashboard data. Please refresh the page." />
      </div>
    );
  }

  const overview = stats.overview || {};
  const users = overview.users || {};
  const providers = overview.providers || {};
  const services = overview.services || {};
  const revenue = overview.revenue || {};
  const breakdown = stats.breakdown || {};

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back! Here's what's happening today.</p>
        </div>
        <button
          onClick={fetchDashboardData}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={users.total || 0}
          icon="users"
          color="blue"
          trend={`${users.active || 0} active`}
        />
        <StatCard
          title="Hospitals"
          value={providers.hospitals?.total || 0}
          icon="hospital"
          color="green"
          link="/admin/hospitals"
          trend={`${providers.hospitals?.pending || 0} pending`}
        />
        <StatCard
          title="Pharmacies"
          value={providers.pharmacies?.total || 0}
          icon="pharmacy"
          color="purple"
          link="/admin/pharmacies"
          trend={`${providers.pharmacies?.pending || 0} pending`}
        />
        <StatCard
          title="Revenue"
          value={`KES ${(revenue.total || 0).toLocaleString()}`}
          icon="money"
          color="orange"
          trend={`${revenue.transactions || 0} transactions`}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Consultations</h3>
            <span className="text-2xl font-bold text-indigo-600">
              {services.consultations?.total || 0}
            </span>
          </div>
          <div className="space-y-2">
            {services.consultations?.byStatus?.map((status) => (
              <div key={status._id} className="flex justify-between text-sm">
                <span className="text-gray-600 capitalize">{status._id}</span>
                <span className="font-medium text-gray-900">{status.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Orders</h3>
            <span className="text-2xl font-bold text-purple-600">
              {services.orders?.total || 0}
            </span>
          </div>
          <div className="space-y-2">
            {services.orders?.byStatus?.map((status) => (
              <div key={status._id} className="flex justify-between text-sm">
                <span className="text-gray-600 capitalize">{status._id}</span>
                <span className="font-medium text-gray-900">{status.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Ambulances</h3>
            <span className="text-2xl font-bold text-red-600">
              {providers.ambulances?.total || 0}
            </span>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Active</span>
              <span className="font-medium text-green-600">{providers.ambulances?.active || 0}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Verified</span>
              <span className="font-medium text-blue-600">{providers.ambulances?.verified || 0}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Pending</span>
              <span className="font-medium text-yellow-600">{providers.ambulances?.pending || 0}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link
            to="/admin/users"
            className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all text-center"
          >
            <svg className="w-8 h-8 mx-auto mb-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <span className="text-sm font-medium">Manage Users</span>
          </Link>

          <Link
            to="/admin/hospitals"
            className="p-4 border border-gray-200 rounded-lg hover:border-green-500 hover:shadow-md transition-all text-center"
          >
            <svg className="w-8 h-8 mx-auto mb-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="text-sm font-medium">Hospitals</span>
          </Link>

          <Link
            to="/admin/pharmacies"
            className="p-4 border border-gray-200 rounded-lg hover:border-purple-500 hover:shadow-md transition-all text-center"
          >
            <svg className="w-8 h-8 mx-auto mb-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span className="text-sm font-medium">Pharmacies</span>
          </Link>

          <Link
            to="/admin/analytics"
            className="p-4 border border-gray-200 rounded-lg hover:border-indigo-500 hover:shadow-md transition-all text-center"
          >
            <svg className="w-8 h-8 mx-auto mb-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span className="text-sm font-medium">Analytics</span>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Recent Activity</h2>
            <Link to="/admin/users" className="text-sm text-blue-600 hover:text-blue-700">
              View All →
            </Link>
          </div>
          <div className="space-y-3">
            {breakdown?.recentActivity && breakdown.recentActivity.length > 0 ? (
              breakdown.recentActivity.slice(0, 5).map((user) => (
                <div key={user._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-semibold">
                        {user.name?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{user.name || 'Unknown User'}</p>
                      <p className="text-sm text-gray-600">{user.email || 'No email'}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded capitalize">
                      {user.role || 'user'}
                    </span>
                    {user.isVerified && (
                      <div className="flex items-center justify-end mt-1">
                        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : recentActivity && recentActivity.length > 0 ? (
              recentActivity.slice(0, 5).map((activity, idx) => (
                <div key={activity.data?._id || idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      activity.type === 'user_registration' ? 'bg-blue-100' : 
                      activity.type === 'consultation' ? 'bg-green-100' : 'bg-purple-100'
                    }`}>
                      {activity.type === 'user_registration' && (
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      )}
                      {activity.type === 'consultation' && (
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      )}
                      {activity.type === 'order' && (
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">{activity.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(activity.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>No recent activity</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Pending Verifications</h2>
            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
              {users.pending || 0} pending
            </span>
          </div>
          <div className="space-y-3">
            {users.pending > 0 ? (
              <Link
                to="/admin/users?verified=false"
                className="block p-4 border-2 border-dashed border-yellow-300 rounded-lg hover:border-yellow-400 text-center"
              >
                <svg className="w-12 h-12 mx-auto mb-2 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-sm font-medium text-gray-700">
                  Review {users.pending} pending verification{users.pending > 1 ? 's' : ''}
                </p>
              </Link>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-16 h-16 mx-auto mb-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>All users verified!</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {breakdown.usersByRole && breakdown.usersByRole.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">User Distribution by Role</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {breakdown.usersByRole.map((role) => (
              <div key={role._id} className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-3xl font-bold text-gray-900">{role.count || 0}</p>
                <p className="text-sm text-gray-600 capitalize mt-1">{role._id || 'Unknown'}</p>
                <div className="mt-2 text-xs text-gray-500">
                  <span className="text-green-600">{role.active || 0} active</span>
                  {' · '}
                  <span className="text-blue-600">{role.verified || 0} verified</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {revenue.total > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Revenue Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Today</p>
              <p className="text-2xl font-bold text-green-600">
                KES {(revenue.today || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">{revenue.todayTransactions || 0} transactions</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">This Week</p>
              <p className="text-2xl font-bold text-blue-600">
                KES {(revenue.thisWeek || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">{revenue.weekTransactions || 0} transactions</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">This Month</p>
              <p className="text-2xl font-bold text-purple-600">
                KES {(revenue.thisMonth || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">{revenue.monthTransactions || 0} transactions</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
              <p className="text-2xl font-bold text-orange-600">
                KES {(revenue.total || 0).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">{revenue.transactions || 0} transactions</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;